import { z } from 'zod';

/**
 * Papéis do cockpit, mapeados a partir dos grupos do Cognito.
 *
 * Moram aqui, e não na API, porque a tela precisa dos mesmos nomes para montar o
 * formulário de operadores — e nome de papel divergente entre frontend e backend vira
 * permissão que a tela oferece e o servidor recusa.
 */
export const PAPEIS = ['closer', 'curadoria', 'admin'] as const;
export const papelSchema = z.enum(PAPEIS);
export type Papel = (typeof PAPEIS)[number];

export const DESCRICAO_DO_PAPEL: Record<Papel, string> = {
  closer: 'Cadastra candidatos, emite documentos e gera links de cobrança.',
  curadoria: 'Decide aprovação e recusa no comitê.',
  admin: 'Tudo, inclusive excluir candidato e administrar operadores.',
};

/**
 * Situação da conta no Cognito, traduzida para o que o operador da tela precisa saber.
 *
 *  convidado  — criado, senha temporária enviada, primeiro acesso pendente
 *  ativo      — em uso
 *  desativado — bloqueado, sem conseguir entrar
 *  expirado   — a senha temporária venceu antes do primeiro acesso
 */
export const SITUACOES_OPERADOR = ['convidado', 'ativo', 'desativado', 'expirado'] as const;
export type SituacaoOperador = (typeof SITUACOES_OPERADOR)[number];

export interface OperadorDaPlataforma {
  /** `sub` do Cognito. Identidade estável, usada na trilha e nas URLs da API. */
  id: string;
  email: string;
  nome: string;
  papeis: Papel[];
  situacao: SituacaoOperador;
  /** Se o segundo fator já foi cadastrado. Sem ele, produção não deixa entrar. */
  mfaCadastrado: boolean;
  criadoEm: string;
  atualizadoEm: string;
}

export const criarOperadorSchema = z
  .object({
    email: z.string().trim().toLowerCase().email('E-mail inválido.'),
    nome: z
      .string()
      .trim()
      .min(3, 'Informe o nome completo.')
      .max(120, 'Nome longo demais.')
      // O nome vai para a trilha de auditoria de cada documento e cobrança emitidos.
      .refine((v) => v.includes(' '), { message: 'Informe nome e sobrenome.' }),
    papeis: z.array(papelSchema).min(1, 'Escolha ao menos um papel.'),
  })
  .strict();
export type CriarOperador = z.infer<typeof criarOperadorSchema>;

export const papeisSchema = z.object({ papeis: z.array(papelSchema).min(1) }).strict();

export const situacaoSchema = z
  .object({
    ativo: z.boolean(),
    motivo: z.string().trim().min(10, 'Descreva o motivo — ele fica na trilha.').max(300),
  })
  .strict();

/**
 * Se a mudança deixaria a plataforma sem nenhum administrador.
 *
 * Não é zelo excessivo: sem admin não há quem crie operador, promova alguém ou reative
 * uma conta — o conserto passa a exigir acesso à console da AWS. Vale tanto para tirar o
 * papel de admin quanto para desativar a conta de um.
 */
export function ficariaSemAdmin(
  operadores: Pick<OperadorDaPlataforma, 'id' | 'papeis' | 'situacao'>[],
  alterado: { id: string; papeis?: Papel[]; ativo?: boolean },
): boolean {
  const admins = operadores.filter((o) => {
    if (o.id === alterado.id) {
      const papeis = alterado.papeis ?? o.papeis;
      const ativo = alterado.ativo ?? o.situacao !== 'desativado';
      return papeis.includes('admin') && ativo;
    }
    return o.papeis.includes('admin') && o.situacao !== 'desativado';
  });

  return admins.length === 0;
}
